import { Component, type ReactNode } from "react";

import * as amplitude from "@amplitude/analytics-browser";
import { Identify } from "@amplitude/analytics-browser";

import { shouldTrackCustomEventInGlobal } from "~/v2/analytics/globalAmplitudeEventPolicy";
import { normalizeGrowSurfReferrerID } from "~/v2/api/auth/utm";
import type { Account, OwnerDetails } from "~/v2/api/rest/account";

interface AmplitudeEventDetail {
  "amplitude-event-name"?: string;
  "amplitude-event-data"?: string | Record<string, unknown>;
  "amplitude-event-options"?: AmplitudeEventOptions;
}

interface AmplitudeEventData {
  url?: string;
  is_paper?: boolean;
  [key: string]: unknown;
}

interface AmplitudeEventOptions {
  user_id?: string;
  user_properties?: ReturnType<Identify["getUserProperties"]>;
}

interface AmplitudeAccountData {
  liveAccount?: Account;
  details?: OwnerDetails;
}

interface AmplitudeProviderProps {
  children: ReactNode;
}

// Global Website Amplitude Instance
let globalAmplitude: ReturnType<typeof amplitude.createInstance> | undefined;

// Initialize Amplitude
const initAmplitude = (): void => {
  const options: { defaultTracking: boolean; deviceId?: string } = {
    defaultTracking: true,
  };
  const deviceId = new URLSearchParams(window?.location?.search ?? "").get(
    "amp_device_id",
  );
  if (deviceId) {
    options.deviceId = deviceId;
  }
  amplitude.init(window?.env?.AMPLITUDE_KEY ?? "", options);

  const globalKey = window?.env?.AMPLITUDE_GLOBAL_KEY;
  if (!globalKey) {
    return;
  }

  globalAmplitude = amplitude.createInstance();
  globalAmplitude.init(globalKey, {
    autocapture: {
      attribution: true,
      elementInteractions: {
        visualTaggingOptions: {
          enabled: false,
        },
      },
      fileDownloads: true,
      formInteractions: true,
      frustrationInteractions: true,
      networkTracking: {
        captureRules: [
          {
            requestHeaders: false,
            responseHeaders: false,
            statusCodeRange: "500-599",
          },
        ],
        ignoreAmplitudeRequests: true,
      },
      pageUrlEnrichment: true,
      pageViews: {
        trackHistoryChanges: "pathOnly",
      },
      sessions: true,
      webVitals: true,
    },
    cookieOptions:
      window?.env?.ENV === "PROD"
        ? {
            domain: ".alpaca.markets",
            sameSite: "Lax",
          }
        : {
            sameSite: "Lax",
          },
    deviceId: deviceId ?? undefined,
    enableDiagnostics: false,
    instanceName: "alpaca-markets-global",
    remoteConfig: {
      fetchRemoteConfig: false,
    },
  });
};

// Initialize on module load
initAmplitude();

class AmplitudeProvider extends Component<AmplitudeProviderProps> {
  eventTypes: string[] = [
    "click",
    "input",
    "submit",
    "load",
    "unload",
    "custom",
  ];
  eventHistory: Map<string, number> = new Map();
  eventHistoryTimeout: number = 60000;
  static account: AmplitudeAccountData | undefined;

  static dispatch(
    name: AmplitudeEventDetail["amplitude-event-name"],
    data?: AmplitudeEventDetail["amplitude-event-data"],
    options?: AmplitudeEventDetail["amplitude-event-options"],
  ): void {
    window.dispatchEvent(
      new CustomEvent<AmplitudeEventDetail>("custom", {
        detail: {
          "amplitude-event-name": name,
          "amplitude-event-data": data,
          "amplitude-event-options": options,
        },
      }),
    );
  }

  static dispatchPageVisit(eventData: AmplitudeEventData = {}): void {
    if (!Object.hasOwn(eventData, "url")) {
      eventData.url = window.location.pathname;
    }

    window.dispatchEvent(
      new CustomEvent<AmplitudeEventDetail>("custom", {
        detail: {
          "amplitude-event-name": "algodash_page_visited",
          "amplitude-event-data": eventData,
        },
      }),
    );
  }

  static setAccount = (acc: AmplitudeAccountData | undefined): void => {
    AmplitudeProvider.account = acc;

    const amp = getAmplitude();

    if (!amp) {
      return;
    }

    amp.setUserId(acc?.details?.owner_id ?? undefined);
    globalAmplitude?.setUserId(acc?.details?.owner_id ?? undefined);

    const identifyObj = new Identify();
    identifyObj.set("algodash_v2", true);

    const growsurfReferrerID = normalizeGrowSurfReferrerID(
      acc?.details?.utm_params?.growsurf_referrer_id,
    );
    if (growsurfReferrerID) {
      identifyObj.setOnce("growsurf_referrer_id", growsurfReferrerID);
    }

    if (acc?.liveAccount?.elite_status) {
      identifyObj.set("elite_status", acc.liveAccount.elite_status);
    }

    if (acc?.details?.country_of_tax_residence) {
      identifyObj.set(
        "country_of_tax_residence",
        acc.details.country_of_tax_residence,
      );
    }

    if (acc?.liveAccount?.status) {
      identifyObj.set("account_status", acc.liveAccount.status);
    }

    if (acc?.liveAccount?.account_number) {
      identifyObj.set("account_number", acc.liveAccount.account_number);
    }

    if (acc?.liveAccount?.id) {
      identifyObj.set("account_id", acc.liveAccount.id);
    }

    amp.identify(identifyObj);
    globalAmplitude?.identify(identifyObj);
  };

  static dispatchKYCComplete = (acc: AmplitudeAccountData): void => {
    AmplitudeProvider.setAccount(acc);
    const ownerID = acc.details?.owner_id ?? undefined;
    const growsurfReferrerID = normalizeGrowSurfReferrerID(
      acc.details?.utm_params?.growsurf_referrer_id,
    );
    const referralIdentify = growsurfReferrerID
      ? new Identify().setOnce("growsurf_referrer_id", growsurfReferrerID)
      : undefined;

    AmplitudeProvider.dispatch("onboardingv2_kyc_complete", undefined, {
      ...(ownerID && { user_id: ownerID }),
      ...(referralIdentify && {
        user_properties: referralIdentify.getUserProperties(),
      }),
    });
  };

  // WARNING: Do not remove! This class is a wrapper for the rest of the app.
  render = (): ReactNode => this.props.children;

  componentDidMount = (): void => {
    this.eventTypes.forEach((type) => {
      window.addEventListener(type, this.handleEvent.bind(this), false);
    });
  };

  handleEvent = (event: Event): void => {
    const eventName = this.getEventName(event);
    const eventData: AmplitudeEventData = this.getEventData(event) || {};
    const eventOptions = this.getEventOptions(event);
    const isPaper = window.location.href.indexOf("paper") > -1;

    if (isPaper && !Object.hasOwn(eventData, "is_paper")) {
      eventData.is_paper = true;
    }

    const key = `${eventName}-${JSON.stringify(eventData)}-${JSON.stringify(eventOptions)}`;
    const rateExceeded =
      Date.now() - (this.eventHistory.get(key) ?? 0) < this.eventHistoryTimeout;

    // If we have an event name and data (optional)
    // If we have not exceeded the rate limit
    if (eventName && !rateExceeded) {
      const amplitudeEvent = eventOptions
        ? {
            event_type: eventName,
            event_properties: eventData,
            ...eventOptions,
          }
        : eventName;

      if (typeof amplitudeEvent === "string") {
        getAmplitude()?.track(amplitudeEvent, eventData);
      } else {
        getAmplitude()?.track(amplitudeEvent);
      }
      if (globalAmplitude && shouldTrackCustomEventInGlobal(eventName)) {
        if (typeof amplitudeEvent === "string") {
          globalAmplitude.track(amplitudeEvent, eventData);
        } else {
          globalAmplitude.track(amplitudeEvent);
        }
      }
      this.eventHistory.set(key, Date.now());
    }

    // Clear expired events
    this.eventHistory.forEach((timestamp, key) => {
      if (Date.now() - timestamp > this.eventHistoryTimeout) {
        this.eventHistory.delete(key);
      }
    });
  };

  getEventName = (event: Event): string | null => {
    const target = event.target as HTMLElement | null;
    const detail = (event as CustomEvent<AmplitudeEventDetail>).detail;
    const attribute = "amplitude-event-name";

    if (target && "getAttribute" in target) {
      return target.getAttribute(attribute);
    }

    if (detail) {
      return detail[attribute] ?? null;
    }

    return null;
  };

  getEventData = (event: Event): AmplitudeEventData | null => {
    const target = event.target as HTMLElement | null;
    const detail = (event as CustomEvent<AmplitudeEventDetail>).detail;

    if (target && "getAttribute" in target) {
      try {
        const attrValue = target.getAttribute("amplitude-event-data");
        if (attrValue) {
          return JSON.parse(attrValue) as AmplitudeEventData;
        }
      } catch {
        // ignore
      }
    }

    if (
      detail &&
      typeof detail === "object" &&
      "amplitude-event-data" in detail
    ) {
      const data = detail["amplitude-event-data"];

      if (typeof data === "string") {
        try {
          return JSON.parse(data) as AmplitudeEventData;
        } catch {
          return null;
        }
      }
      return data as AmplitudeEventData;
    }

    return null;
  };

  getEventOptions = (event: Event): AmplitudeEventOptions | undefined => {
    const detail = (event as CustomEvent<AmplitudeEventDetail>).detail;
    return detail?.["amplitude-event-options"];
  };
}

export const getAmplitude = (): typeof amplitude => amplitude;

export default AmplitudeProvider;
